import { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import SocialWall from './SocialWall';
import { OPENING_HOURS, SITE } from '../siteConfig';
import '../style/BarCamPage.css';
import '../style/BarCamPage.details.css';
import '../style/BarCamPage.social.css';
import '../style/BarCamPage.modals.css';
import '../style/BarCamPage.responsive.css';

const SOCIAL_PREFERENCE_KEY = 'patricks-social-embeds';

const readSocialPreference = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const value = window.localStorage.getItem(SOCIAL_PREFERENCE_KEY);
    return value === 'enabled' || value === 'disabled' ? value : null;
  } catch {
    return null;
  }
};

const FeatureIcon = ({ children }) => (
  <span className="feature-icon" aria-hidden="true">
    {children}
  </span>
);

const BarCamPage = () => {
  const [activeModal, setActiveModal] = useState(null);
  const [cameraStatus, setCameraStatus] = useState('idle');
  const [cameraImageSrc, setCameraImageSrc] = useState('');
  const [socialPreference, setSocialPreference] = useState(readSocialPreference);

  const todayIndex = useMemo(() => new Date().getDay(), []);
  const exteriorImage = require('../assets/aa.jpg');
  const mixedContentBlocked =
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    SITE.webcamUrl.startsWith('http://');

  useEffect(() => {
    document.title = "Patrick's Bar | Bangor";
  }, []);

  useEffect(() => {
    if (activeModal !== 'webcam' || mixedContentBlocked) {
      return undefined;
    }

    const refreshCamera = () => {
      const separator = SITE.webcamUrl.includes('?') ? '&' : '?';
      setCameraImageSrc(`${SITE.webcamUrl}${separator}t=${Date.now()}`);
    };

    setCameraStatus('loading');
    refreshCamera();
    const interval = window.setInterval(refreshCamera, 2000);

    return () => window.clearInterval(interval);
  }, [activeModal, mixedContentBlocked]);

  const updateSocialPreference = (preference) => {
    setSocialPreference(preference);
    try {
      if (preference) {
        window.localStorage.setItem(SOCIAL_PREFERENCE_KEY, preference);
      } else {
        window.localStorage.removeItem(SOCIAL_PREFERENCE_KEY);
      }
    } catch {
      // The preference still works for the current visit if storage is unavailable.
    }
  };

  const openSocialSettings = () => {
    updateSocialPreference(null);
    document.getElementById('social')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div id="top" className="site-shell">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>

      <header className="site-header">
        <div className="site-header__inner">
          <a className="brand" href="#top" aria-label="Patrick's Bar home">
            <span className="brand__shamrock" aria-hidden="true">☘</span>
            <span className="brand__name">Patrick&apos;s Bar</span>
            <span className="brand__place">Bangor</span>
          </a>

          <nav className="site-nav" aria-label="Primary navigation">
            <a href="#about">The pub</a>
            <a href="#hours">Hours</a>
            <a href="#social">What&apos;s on</a>
            <a href="#visit">Find us</a>
            <button type="button" onClick={() => setActiveModal('webcam')}>
              BarCam
            </button>
          </nav>
        </div>
      </header>

      <main id="main-content">
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero__inner">
            <div className="hero__copy">
              <p className="eyebrow">A proper local in Upper Bangor</p>
              <h1 id="hero-title">
                Patrick&apos;s Bar
                <span>Good pints. Live music. Proper company.</span>
              </h1>
              <p className="hero__intro">
                A characterful independent pub for locals, students and anyone who
                appreciates a well-kept pint, sport on the screens and a bar with
                stories in every corner.
              </p>
              <div className="hero__actions">
                <a className="button button--brass" href="#social">
                  See what&apos;s happening
                </a>
                <button
                  type="button"
                  className="button button--outline"
                  onClick={() => setActiveModal('webcam')}
                >
                  Open the BarCam
                </button>
              </div>
              <p className="hero__address">
                <span aria-hidden="true">●</span> {SITE.address.street}, {SITE.address.town}
              </p>
            </div>

            <figure className="hero-photo">
              <div className="hero-photo__frame">
                <img
                  src={exteriorImage}
                  alt="The bright green frontage of Patrick's Bar on Holyhead Road in Bangor"
                  width="480"
                  height="360"
                />
              </div>
              <figcaption>
                <span aria-hidden="true">☘</span> You cannot miss the green frontage.
              </figcaption>
            </figure>
          </div>
        </section>

        <section id="about" className="section section--about" aria-labelledby="about-title">
          <div className="section-heading">
            <p className="eyebrow">More pub, less polished brochure</p>
            <h2 id="about-title">A bar that actually looks and feels lived in</h2>
            <p>
              Warm timber, pub signs, packed shelves, regular faces and spontaneous
              music sessions. Patrick&apos;s is at its best when the room is full and
              everybody has joined the conversation.
            </p>
          </div>

          <div className="feature-grid">
            <article className="feature-card">
              <FeatureIcon>♫</FeatureIcon>
              <h3>Music and nights out</h3>
              <p>Live music, informal sessions and karaoke when the mood takes over.</p>
            </article>
            <article className="feature-card">
              <FeatureIcon>◉</FeatureIcon>
              <h3>Sport on the screens</h3>
              <p>Big sporting moments shown in the kind of room that reacts together.</p>
            </article>
            <article className="feature-card">
              <FeatureIcon>◆</FeatureIcon>
              <h3>Changing real ales</h3>
              <p>Three hand pumps with a rotating selection, including North Wales beers.</p>
            </article>
          </div>
        </section>

        <section id="hours" className="section section--details" aria-labelledby="details-title">
          <div className="section-heading section-heading--compact">
            <p className="eyebrow">Plan your visit</p>
            <h2 id="details-title">Opening hours and the live BarCam</h2>
          </div>

          <div className="details-grid">
            <article className="detail-card detail-card--hours">
              <div className="detail-card__heading">
                <div>
                  <p className="detail-card__label">Opening times</p>
                  <h3>Seven days a week</h3>
                </div>
                <span className="detail-card__mark" aria-hidden="true">☘</span>
              </div>
              <ul className="hours-list">
                {OPENING_HOURS.map(({ day, dayIndex, hours }) => (
                  <li key={day} className={dayIndex === todayIndex ? 'is-today' : ''}>
                    <span>
                      {day}
                      {dayIndex === todayIndex && <small>Today</small>}
                    </span>
                    <strong>{hours}</strong>
                  </li>
                ))}
              </ul>
              <p className="detail-card__note">
                Event and bank-holiday hours can vary. Check the latest social post
                before making a special journey.
              </p>
            </article>

            <article className="detail-card detail-card--camera">
              <div className="camera-illustration" aria-hidden="true">
                <span className="camera-illustration__light" />
                <span className="camera-illustration__lens" />
              </div>
              <div className="detail-card__camera-copy">
                <p className="detail-card__label">Live BarCam</p>
                <h3>Have a look before you head over</h3>
                <p>
                  Open the live camera for a quick view of what is happening at the bar.
                </p>
                <button
                  type="button"
                  className="button button--green"
                  onClick={() => setActiveModal('webcam')}
                >
                  View the BarCam
                </button>
              </div>
            </article>
          </div>
        </section>

        <SocialWall
          preference={socialPreference}
          onPreferenceChange={updateSocialPreference}
        />

        <section id="visit" className="section section--visit" aria-labelledby="visit-title">
          <div className="visit-card">
            <div className="visit-card__copy">
              <p className="eyebrow">Find the green pub</p>
              <h2 id="visit-title">Come and see us in Upper Bangor</h2>
              <address>
                <strong>{SITE.name}</strong>
                <span>{SITE.address.street}</span>
                <span>{SITE.address.town}</span>
                <span>{SITE.address.postcode}</span>
              </address>
              <p>
                Close to Bangor railway station and on the route towards the Menai Strait.
              </p>
              <div className="visit-card__actions">
                <button
                  type="button"
                  className="button button--brass"
                  onClick={() => setActiveModal('map')}
                >
                  View map
                </button>
                <a
                  className="button button--ghost-light"
                  href={SITE.mapUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Get directions <span aria-hidden="true">↗</span>
                </a>
              </div>
            </div>
            <div className="visit-card__sign" aria-hidden="true">
              <span>Patrick&apos;s</span>
              <small>Bar · Bangor</small>
              <b>☘</b>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="site-footer__inner">
          <div>
            <p className="site-footer__brand">☘ Patrick&apos;s Bar</p>
            <p>May the road rise to meet you.</p>
          </div>
          <div className="site-footer__links">
            <a href={SITE.social.facebook} target="_blank" rel="noreferrer">Facebook</a>
            <a href={SITE.social.instagram} target="_blank" rel="noreferrer">Instagram</a>
            <button type="button" onClick={openSocialSettings}>Social privacy settings</button>
          </div>
        </div>
      </footer>

      {activeModal === 'webcam' && (
        <Modal title="Live BarCam" onClose={() => setActiveModal(null)} className="modal--camera">
          {mixedContentBlocked ? (
            <div className="camera-message">
              <span className="camera-message__icon" aria-hidden="true">◉</span>
              <h3>The camera is unavailable on the secure website</h3>
              <p>Please check again soon or try opening the camera feed directly.</p>
              <a
                className="button button--green"
                href={SITE.webcamUrl}
                target="_blank"
                rel="noreferrer"
              >
                Open camera feed
              </a>
            </div>
          ) : (
            <div className="camera-viewer">
              {cameraImageSrc && (
                <img
                  src={cameraImageSrc}
                  alt="Live view inside Patrick's Bar"
                  onLoad={() => setCameraStatus('live')}
                  onError={() => setCameraStatus('offline')}
                />
              )}
              {cameraStatus === 'loading' && (
                <p className="camera-viewer__status" role="status">Connecting to the BarCam…</p>
              )}
              {cameraStatus === 'offline' && (
                <div className="camera-message" role="status">
                  <span className="camera-message__icon" aria-hidden="true">◉</span>
                  <h3>The BarCam appears to be offline</h3>
                  <p>Give it a moment and try again later.</p>
                </div>
              )}
              {cameraStatus === 'live' && (
                <p className="camera-live-label"><span /> Live view · refreshes every two seconds</p>
              )}
            </div>
          )}
        </Modal>
      )}

      {activeModal === 'map' && (
        <Modal title="Find Patrick's Bar" onClose={() => setActiveModal(null)} className="modal--map">
          <iframe
            className="map-embed"
            title="Map showing Patrick's Bar on Holyhead Road in Bangor"
            src={SITE.mapEmbedUrl}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </Modal>
      )}
    </div>
  );
};

export default BarCamPage;
